import { useMemo } from "react";

import { ArrowRight, TextLink } from "@/components/ui/Button";
import { useContent } from "@/content-context";
import type { DocumentDoc } from "@/content";

/**
 * The donor document library.
 *
 * What replaced a register of statuses. The page used to list five document
 * types against words like "In preparation" and "Being finalised", which is a
 * project board rather than a library: it told a grant officer what HISCUS had
 * not finished rather than what they could have.
 *
 * This lists documents, grouped by what they are and then by the period they
 * cover, newest first. A document with a file is a download. A document HISCUS
 * will share on request says so and links to the enquiry form. A type with
 * nothing in it is named once, plainly, so a reader can see the shape of what
 * exists without reading a status column.
 *
 * Every row comes from the CMS, so adding an annual report is an upload rather
 * than a deploy.
 */

const CATEGORY_LABELS: Record<string, string> = {
  "annual-report": "Annual reports",
  financial: "Audited financial statements",
  registration: "Registration and legal identity",
  policy: "Policies",
  programme: "Programme reports",
  other: "Other documents",
};

/** The order a funder looks for them in. */
const CATEGORY_ORDER = ["annual-report", "financial", "registration", "programme", "policy", "other"];

export function DocumentLibrary({ contactHref = "/get-involved/support" }: { contactHref?: string }) {
  const { documents } = useContent();

  const grouped = useMemo(() => {
    const byCategory = new Map<string, DocumentDoc[]>();
    for (const doc of documents) {
      const list = byCategory.get(doc.category) ?? [];
      list.push(doc);
      byCategory.set(doc.category, list);
    }

    return CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((category) => ({
      category,
      label: CATEGORY_LABELS[category] ?? category,
      // Newest period first. A document with no period sorts last rather than
      // claiming a date it does not have.
      items: (byCategory.get(category) ?? [])
        .slice()
        .sort((a, b) => (b.period ?? "").localeCompare(a.period ?? "")),
    }));
  }, [documents]);

  if (grouped.length === 0) {
    return (
      <div className="rounded-[16px] border border-line bg-white p-6">
        <h3 style={{ fontSize: "var(--step-h4)" }}>Documents for due diligence</h3>
        <p className="mt-3 text-muted">
          Annual reports, audited financial statements and registration certificates are shared
          directly with funders and institutional partners. Tell us what your process needs and you
          will be told what can be supplied and when.
        </p>
        <div className="mt-5">
          <TextLink href={contactHref}>Request documentation</TextLink>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {grouped.map((group) => (
        <section key={group.category}>
          <h3 className="text-[0.6875rem] font-semibold uppercase tracking-[0.2em] text-blue">
            {group.label}
          </h3>
          <ul className="mt-4 grid gap-4 sm:grid-cols-2">
            {group.items.map((doc) => (
              <li key={`${doc.title}-${doc.period ?? ""}`} className="surface rounded-[16px] p-5">
                <h4 style={{ fontSize: "var(--step-h4)" }}>{doc.title}</h4>
                {doc.period ? <p className="mt-1 text-[0.875rem] text-muted">{doc.period}</p> : null}
                {doc.description ? (
                  <p className="mt-2.5 text-[0.9375rem] leading-relaxed text-muted">{doc.description}</p>
                ) : null}

                {doc.availability === "download" && doc.fileUrl ? (
                  <a
                    href={doc.fileUrl}
                    className="mt-4 inline-flex min-h-11 items-center gap-2 text-[0.9375rem] font-semibold text-navy underline underline-offset-4"
                  >
                    Download
                    {doc.fileSize ? (
                      <span className="font-normal text-muted">({Math.round(doc.fileSize / 1024)} KB)</span>
                    ) : null}
                  </a>
                ) : (
                  <p className="mt-4 text-[0.9375rem]">
                    <a
                      href={contactHref}
                      className="inline-flex min-h-11 items-center gap-2 font-semibold text-navy"
                    >
                      Request a copy
                      <ArrowRight />
                    </a>
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
